import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { ApiKeyMetadata, PatchApiKey, PostApiKey } from '../../types/api.types'
import keysApi from '../../service/keys.api'
import { ErrorCodes, showError } from '../../utils/ErrorHandler'
import { isAxiosError } from 'axios'

export interface ApiKeysState {
  keys: ApiKeyMetadata[]
  loading: boolean
  creatingKey: boolean
  error: string | null
}

export const fetchApiKeys = createAsyncThunk('auth/fetchApiKeys', async (_, { rejectWithValue }) => {
  try {
    return await keysApi.getApiKeys()
  } catch (error) {
    showError(ErrorCodes.FETCH_API_KEYS_FAILED, error)
    return rejectWithValue('fetchApiKeysFailed')
  }
})

export const createApiKey = createAsyncThunk<ApiKeyMetadata, PostApiKey>(
  'apiKeys/createApiKey',
  async (payload: PostApiKey, { rejectWithValue }) => {
    let result: { apiKey: ApiKeyMetadata; redirectUrl?: string }
    try {
      result = await keysApi.createApiKey(payload)
    } catch (error) {
      if (isAxiosError(error) && error.response && keysApi.isProviderRejectedResponse(error.response)) {
        showError(ErrorCodes.API_KEY_REJECTED, error)
        return rejectWithValue('providerRejectedApiKey')
      } else {
        showError(ErrorCodes.CREATE_API_KEY_FAILED, error)
        return rejectWithValue('createApiKeyFailed')
      }
    }

    if (result.redirectUrl) {
      const url = new URL(result.redirectUrl)
      // TODO: Remove lowercase notation once all providers migrated
      url.searchParams.set('referenceid', payload.referenceId)
      url.searchParams.set('referenceId', payload.referenceId)
      window.location.href = url.toString()
    }
    return result.apiKey
  },
)

export const updateApiKey = createAsyncThunk<ApiKeyMetadata, { id: string; context: PatchApiKey }>(
  'apiKeys/updateApiKey',
  async ({ id, context }, { rejectWithValue }) => {
    try {
      return keysApi.updateApiKey(id, context)
    } catch (error) {
      showError(ErrorCodes.UPDATE_API_KEY_FAILED, error)
      return rejectWithValue('updateApiKeyFailed')
    }
  },
)

export const deleteApiKey = createAsyncThunk<void, string>('apiKeys/deleteApiKey', async (id, { rejectWithValue }) => {
  try {
    await keysApi.deleteApiKey(id)
  } catch (error) {
    showError(ErrorCodes.DELETE_API_KEY_FAILED, error)
    return rejectWithValue('deleteApiKeyFailed')
  }
})
const initialState: ApiKeysState = {
  keys: [],
  loading: false,
  creatingKey: false,
  error: null,
}

const apiKeySlice = createSlice({
  name: 'apiKeys',
  initialState,
  reducers: {},

  extraReducers: (builder) => {
    builder
      .addCase(fetchApiKeys.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchApiKeys.fulfilled, (state, action) => {
        state.loading = false
        state.keys = action.payload
      })
      .addCase(fetchApiKeys.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Error fetching API keys'
      })

      .addCase(createApiKey.pending, (state) => {
        state.creatingKey = true
      })
      .addCase(createApiKey.fulfilled, (state, action) => {
        state.keys = [...state.keys, action.payload]
        state.creatingKey = false
      })
      .addCase(createApiKey.rejected, (state, action) => {
        state.error = action.error.message || 'Error creating API key'
        state.creatingKey = false
      })

      .addCase(updateApiKey.fulfilled, (state, action) => {
        state.keys = [...state.keys.filter((key) => key.id !== action.payload.id), action.payload]
      })
      .addCase(updateApiKey.rejected, (state, action) => {
        state.error = action.error.message || 'Error updating API key'
      })

      .addCase(deleteApiKey.fulfilled, (state, action) => {
        const apiKeyId = action.meta.arg
        state.keys = state.keys.filter((key) => key.id !== apiKeyId)
      })
      .addCase(deleteApiKey.rejected, (state, action) => {
        state.error = action.error.message || 'Error deleting API key'
      })
  },
})
export const apiKeyReducer = apiKeySlice.reducer
